import React, { useState } from 'react';
import {
  Box,
  Fade,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';

const colors = {
  ink: "#06185f",
  muted: "#4c5d86",
  blue: "#0059f6",
  line: "#d9e3f4",
};

const filters = [
  { key: "TOTAL", label: "Totales", icon: GroupsOutlinedIcon },
  { key: "NUEVOS", label: "Nuevos", icon: NewReleasesOutlinedIcon },
  { key: "ATRASADOS", label: "Atrasados", icon: ScheduleOutlinedIcon },
];

function MetricCard({ label, value, icon: Icon, active, onClick }) {
  return (
    <Paper
      role="button"
      elevation={0}
      onClick={onClick}
      sx={{
        minWidth: { xs: 118, sm: 128 },
        height: 64,
        px: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.4,
        cursor: "pointer",
        userSelect: "none",
        borderRadius: "8px",
        backgroundColor: "rgba(255,255,255,0.94)",
        border: active ? `1px solid ${colors.blue}` : "1px solid #dce5f2",
        boxShadow: active
          ? "0 8px 18px rgba(0, 89, 246, 0.10)"
          : "0 8px 18px rgba(19, 39, 84, 0.07)",
        transition: "transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
        "&:hover": {
          transform: "translateY(-1px)",
          borderColor: "rgba(0,89,246,0.38)",
          boxShadow: "0 10px 22px rgba(19, 39, 84, 0.10)",
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          color: colors.blue,
          backgroundColor: "#edf4ff",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: colors.muted, fontSize: 10.5, fontWeight: 800, lineHeight: 1.1 }}>
          {label}
        </Typography>
        <Typography sx={{ color: colors.ink, fontSize: 20, fontWeight: 900, lineHeight: 1.15 }}>
          {value}
        </Typography>
      </Box>
    </Paper>
  );
}

export default function PrincipalLeadsHeader({
  total = 0,
  nuevos = 0,
  atrasados = 0,
  vendedores = [],
  selectedVendedorId = "",
  onChangeVendedor = () => { },
  vendedoresLoading = false,
  exclusiveFilter = "TOTAL",
  onChangeExclusiveFilter = () => { },
  isFullScreen = false,
  onToggleFullScreen,
  actions = null,
}) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const metricValues = {
    TOTAL: total,
    NUEVOS: nuevos,
    ATRASADOS: atrasados,
  };

  const HeaderContent = (
    <Box sx={{ width: "100%" }}>
      {!isFullScreen && (
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                color: colors.ink,
                fontSize: 26,
                fontWeight: 900,
                letterSpacing: "-0.7px",
                lineHeight: 1.1,
              }}
            >
              Leads
            </Typography>
            <Typography sx={{ mt: 0.5, color: colors.muted, fontSize: 12, fontWeight: 500 }}>
              Supervisá el estado comercial y el seguimiento del equipo.
            </Typography>
          </Box>

          {actions && (
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {actions}
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            py: 0.7,
            px: 0.1,
            mt: -0.7,
            mb: -0.7,
            overflowX: { xs: "auto", md: "visible" },
            overflowY: "visible",
          }}
        >
          {filters.map((filter) => (
            <MetricCard
              key={filter.key}
              label={filter.label}
              value={metricValues[filter.key]}
              icon={filter.icon}
              active={exclusiveFilter === filter.key}
              onClick={() => onChangeExclusiveFilter(filter.key)}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "space-between", md: "flex-end" },
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Typography sx={{ color: colors.muted, fontSize: 12, fontWeight: 700 }}>
            Filtrar por:
          </Typography>
          <Select
            size="small"
            value={selectedVendedorId ?? ""}
            onChange={(e) => onChangeVendedor(e.target.value)}
            onOpen={() => setIsFilterOpen(true)}
            onClose={() => setIsFilterOpen(false)}
            disabled={vendedoresLoading}
            displayEmpty
            sx={{
              minWidth: { xs: 190, sm: 230 },
              height: 38,
              color: colors.ink,
              fontSize: 12,
              fontWeight: 700,
              backgroundColor: "rgba(255,255,255,0.94)",
              borderRadius: "8px",
              boxShadow: "0 10px 22px rgba(19, 39, 84, 0.08)",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#dce5f2",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(0,89,246,0.35)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.blue,
              },
            }}
            renderValue={(v) => {
              if (!v) return "Todos los vendedores";
              const vend = vendedores.find((x) => String(x.id) === String(v));
              return vend?.nombre || vend?.nombreCompleto || `Vendedor #${v}`;
            }}
          >
            <MenuItem value="">Todos los vendedores</MenuItem>
            {vendedores.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                {v.nombre || v.nombreCompleto || `Vendedor #${v.id}`}
              </MenuItem>
            ))}
          </Select>

          <Tooltip title={isFullScreen ? "Salir de vista completa" : "Vista completa (100 filas)"}>
            <IconButton
              onClick={() => onToggleFullScreen && onToggleFullScreen(!isFullScreen)}
              sx={{
                width: 38,
                height: 38,
                color: colors.blue,
                border: "1px solid #dce5f2",
                borderRadius: "8px",
                backgroundColor: "rgba(255,255,255,0.94)",
                boxShadow: "0 10px 22px rgba(19, 39, 84, 0.08)",
                "&:hover": {
                  backgroundColor: "#edf4ff",
                },
              }}
            >
              {isFullScreen ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  if (isFullScreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: '60px',
          left: 0,
          width: '100%',
          zIndex: 1050,
          height: '86px',
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
          '&:hover': { pointerEvents: 'auto' },
          ...(isFilterOpen && { pointerEvents: 'auto' }),
          '&:hover .MuiPaper-root': {
            transform: 'translateY(0)',
            opacity: 1,
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '95%',
            mt: 1,
            px: 2,
            py: 1,
            borderRadius: "10px",
            bgcolor: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            display: 'flex',
            alignItems: 'center',
            transform: 'translateY(-120%)',
            opacity: 0,
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s',
            border: '1px solid #dce5f2',
            boxShadow: '0 18px 38px rgba(19, 39, 84, 0.14)',
            pointerEvents: 'auto',
            ...(isFilterOpen && {
              transform: 'translateY(0)',
              opacity: 1,
            }),
          }}
        >
          {HeaderContent}
        </Paper>

        <Fade in={true} timeout={1000}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '220px',
              height: '6px',
              bgcolor: colors.blue,
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4,
              opacity: 0.8,
              pointerEvents: 'auto',
              cursor: 'pointer',
              zIndex: 1290,
              boxShadow: '0 2px 4px rgba(0,0,0,0.16)',
            }}
          />
        </Fade>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        gap: 3,
      }}
    >
      {HeaderContent}
    </Box>
  );
}
